import { ASSET_RETURNS } from './constants/AssetReturns';
import {
    type CalculatedPortfolioAllocation,
    type CalculatedPortfolioReturns,
    EQUITY_CLASSES,
    FIXED_INCOME_CLASSES,
    type PortfolioAllocation,
} from './types';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class PortfolioCalculator {
    /**
     * Calculate weighted, nominal returns for equities and fixed income based on asset allocation
     * @param portfolio - Portfolio data containing allocations for each asset class
     * @returns Calculated returns for equities and fixed income
     */
    public static calculateReturns(
        portfolio: PortfolioAllocation,
    ): CalculatedPortfolioReturns {
        const equities = this.calculateWeightedReturn(
            portfolio,
            EQUITY_CLASSES,
        );
        const fixedIncome = this.calculateWeightedReturn(
            portfolio,
            FIXED_INCOME_CLASSES,
        );

        return {
            equities,
            fixedIncome,
        };
    }

    /**
     * Calculate total allocations for equities and fixed income based on asset allocation
     * @param portfolio - Portfolio data containing allocations for each asset class
     * @returns Calculated allocations for equities and fixed income
     */
    public static calculateAllocations(
        portfolio: PortfolioAllocation,
    ): CalculatedPortfolioAllocation {
        const equities = this.calculateTotalAllocation(
            portfolio,
            EQUITY_CLASSES,
        );
        const fixedIncome = this.calculateTotalAllocation(
            portfolio,
            FIXED_INCOME_CLASSES,
        );

        return {
            equities,
            fixedIncome,
        };
    }

    /**
     * Calculate weighted return for a specific set of asset classes
     * @param portfolio - Portfolio data containing allocations
     * @param assetClasses - Array of asset classes to include in calculation
     * @returns Weighted return as a decimal (e.g., 0.11 for 11%)
     */
    private static calculateWeightedReturn(
        portfolio: PortfolioAllocation,
        assetClasses: string[],
    ): number {
        let total = 0;
        let weightSum = 0;

        for (const assetClass of assetClasses) {
            const allocation = portfolio[assetClass] ?? 0;
            const returnRate = ASSET_RETURNS[assetClass] ?? 0;
            total += allocation * returnRate;
            weightSum += allocation;
        }

        return weightSum > 0 ? total / weightSum : 0;
    }

    /**
     * Calculate total allocation for a specific set of asset classes
     * @param portfolio - Portfolio data containing allocations
     * @param assetClasses - Array of asset classes to include in calculation
     * @returns Total allocation as a decimal (e.g., 0.8 for 80%)
     */
    private static calculateTotalAllocation(
        portfolio: PortfolioAllocation,
        assetClasses: string[],
    ): number {
        let total = 0;

        for (const assetClass of assetClasses) {
            const allocation = portfolio[assetClass] ?? 0;
            total += allocation;
        }

        return total;
    }
}
